/**
 * FAQ content for /faq.
 *
 * Shape notes:
 * - `a` is always plain, self-contained text. It is what we emit into the
 *   `FAQPage` JSON-LD, so it must read correctly with no surrounding context
 *   and must not depend on the links below it.
 * - `links` renders as a small "see also" row under the answer in the UI and
 *   is deliberately excluded from the structured data.
 *
 * Content rules (kept deliberately, tests pin some of them):
 * - No hard-coded prices or credit amounts — /pricing is the binding source.
 * - No absolute guarantees about third-party systems.
 * - No region codes or sub-processor proper names in the data-processing answer.
 */

export interface FaqLink {
  label: string;
  href: string;
}

export interface FaqItem {
  q: string;
  a: string;
  links?: FaqLink[];
}

export interface FaqCategory {
  id: string;
  category: string;
  items: FaqItem[];
}

export const faqData: FaqCategory[] = [
  {
    id: "about",
    category: "About PaperVerse",
    items: [
      {
        q: "What is PaperVerse?",
        a: "PaperVerse is a research writing and review platform. You write your manuscript in LaTeX with an AI assistant that drafts and revises sections for you, compile it to PDF in the browser, and get a structured AI review that covers methodology, clarity, technical rigour, novelty and references. Write, review, revise — one loop, one place.",
      },
      {
        q: "Who is PaperVerse for?",
        a: "Researchers, PhD students and lab groups who write papers in LaTeX and want feedback before a venue gives it to them. It fits best if you are preparing a conference or journal submission and want to find the weaknesses in your manuscript yourself, first.",
      },
      {
        q: "How is PaperVerse different from using ChatGPT for my paper?",
        a: "A general chat assistant does not see your document. PaperVerse works inside your actual LaTeX project: the assistant edits your source files, compiles the result, shows you a diff of every change, and keeps a version history you can roll back. It also reviews the compiled manuscript the way a conference reviewer would, and your drafts stay private and are processed in the EU.",
      },
      {
        q: "How do I get started?",
        a: "Create a free account. From there you can start a new writing project, upload a PDF of an existing draft for review, or paste an arXiv link to import a published paper. There is nothing to install and no configuration step.",
      },
    ],
  },
  {
    id: "writer",
    category: "Writing with PaperVerse Writer",
    items: [
      {
        q: "What is PaperVerse Writer?",
        a: "PaperVerse Writer is an AI writing assistant for LaTeX manuscripts. You describe what you want in plain language — draft this section, tighten this argument, add related work — and the assistant edits your document, compiles it to PDF and shows you exactly what changed. Writer is currently in a limited beta and is being rolled out gradually.",
      },
      {
        q: "Do I need to know LaTeX?",
        a: "It helps, but you do not have to write it yourself. Projects start from a working template, and you can ask for changes in ordinary language while the assistant handles the LaTeX. When a compile fails, the error is explained in plain language and the assistant attempts the repair.",
      },
      {
        q: "How is Writer different from Overleaf?",
        a: "Overleaf is a collaborative LaTeX editor: you type, it compiles. PaperVerse adds the two things around the typing — an assistant that writes and revises inside the document and explains what it changed, and an AI review of the finished manuscript. If you only want a plain LaTeX editor, Overleaf does that well; PaperVerse is for the drafting and revision loop on top of it.",
      },
      {
        q: "Can Writer compile my paper to PDF?",
        a: "Yes. Every project compiles to PDF in the browser on EU infrastructure, and the PDF updates as the assistant changes the document. If a compile fails, you get the relevant part of the log in readable form rather than a raw dump.",
      },
      {
        q: "Can Writer find and insert references for me?",
        a: "Yes. The assistant can search open bibliographic data, add entries to your bibliography and cite them in the text. Treat every suggestion as a lead, not a fact: language models can state a reference inaccurately or invent one outright, so verify each citation against the original source before you submit.",
      },
      {
        q: "Can I see exactly what the assistant changed?",
        a: "Yes. Each turn produces a diff you can open in the chat, sentence by sentence, with the underlying LaTeX available if you want to inspect it. Every change is also a version, so you can compare versions and go back to an earlier one.",
      },
      {
        q: "Can I import an existing manuscript, and can I export my work?",
        a: "You can bring existing LaTeX source into a project and work on it there, and you can download the compiled PDF at any time. Exporting the raw LaTeX source of a version is a paid-plan feature. Nothing you write is locked into the platform.",
      },
      {
        q: "Is there a watermark on free-plan PDFs?",
        a: "Free-plan PDFs carry a small “Made with PaperVerse | paperverse.io” credit line in the footer of every page. It sits below the text block, does not alter your content and is intentionally discreet. Every paid plan compiles the same draft without it; after upgrading, recompiling the version removes the line and costs no credits. If you are submitting to a venue that rejects third-party footers, use a paid plan for the submission PDF.",
      },

    ],
  },
  {
    id: "reviews",
    category: "AI reviews",
    items: [
      {
        q: "How does the AI review work?",
        a: "The AI reads your manuscript and evaluates it across several dimensions: methodology and experimental design, clarity of writing, technical rigour, novelty, relevance to the field and quality of references. You get a structured report with an overall rating, specific strengths, specific weaknesses and concrete improvement suggestions, in the format a conference or journal review would use.",
      },
      {
        q: "How accurate is the AI feedback?",
        a: "It is a knowledgeable first reader, not a verdict. The review applies consistent criteria every time and reliably surfaces the issues reviewers commonly raise, but it can miss domain-specific problems and it can be wrong. Use it to strengthen the manuscript before human review, not to replace human review.",
      },
      {
        q: "How is AI review different from traditional peer review?",
        a: "Peer review takes weeks or months, depends on reviewer availability, and carries the authority of the venue. An AI review arrives in minutes, is available whenever you need it and applies the same criteria to every paper — but it has no standing with any journal or conference and must never be presented as peer review.",
      },
      {
        q: "Can I review the same paper again after revising?",
        a: "Yes, and that is the intended use. Request a fresh review after each round of revisions to see whether the weaknesses actually closed. Each review of a private manuscript draws on your credit balance; reviews of public arXiv papers are free.",
      },
      {
        q: "Can I use PaperVerse to prepare for conference or journal submissions?",
        a: "That is the most common use case. Researchers use PaperVerse to find weaknesses before submitting to venues such as NeurIPS, ICML or ACL, because the feedback format mirrors what reviewers there produce — which lets you answer the objection in the paper instead of in the rebuttal.",
      },
    ],
  },
  {
    id: "integrity",
    category: "Authorship, integrity and trust",
    items: [
      {
        q: "Who is the author of text the assistant writes?",
        a: "You are. Purely machine-generated text is not protected by copyright in its own right; your own creative contribution is. Authorship, responsibility and attribution stay with you, which also means you are accountable for everything the assistant drafts on your behalf.",
      },
      {
        q: "Do I have to disclose AI assistance to my journal or conference?",
        a: "Often yes, and the rule is set by your venue, not by us. Most major publishers and conferences now require authors to state how generative AI was used and forbid listing an AI system as an author. Check your target venue's policy before submission and follow it — our community guidelines say the same.",
        links: [{ label: "Community guidelines", href: "/guidelines" }],
      },
      {
        q: "Is using an AI writing assistant plagiarism?",
        a: "Not in itself — plagiarism is presenting someone else's work as your own, and the assistant is a tool you direct. It becomes misconduct if you submit text you have not verified, if you fail to disclose AI use where your venue requires it, or if you paste third-party copyrighted text into the assistant to have it reproduced. You remain responsible for the content you submit.",
      },
      {
        q: "Can I trust the citations it suggests?",
        a: "Verify every one. The assistant searches open bibliographic data, but language models can misstate a reference or fabricate one that looks entirely plausible. A fabricated citation in a submitted paper is your problem, not the tool's, so check each reference against the original source.",
      },
      {
        q: "Will my paper be flagged by AI detectors?",
        a: "We cannot promise how any detector will classify a given text, and we do not sell evasion. AI detectors are unreliable in both directions — they flag human writing and miss machine writing. The dependable route is the honest one: do the intellectual work yourself, verify what the assistant produces, and disclose AI assistance where your venue asks for it.",
      },
    ],
  },
  {
    id: "pricing",
    category: "Plans and credits",
    items: [
      {
        q: "Is PaperVerse free to use?",
        a: "There is a free plan with a monthly credit allowance, and reviews of publicly listed arXiv papers are free and unlimited. Paid plans and top-ups are available when you need more. Current plans, allowances and prices are always shown on the pricing page — that page is the binding source of truth, not this FAQ.",
        links: [{ label: "See plans and prices", href: "/pricing" }],
      },
      {
        q: "What are credits and how do they work?",
        a: "One balance covers everything the AI does for you. Each action is metered against what it actually costs to run, so a small edit costs less than a full review. Your plan grants an allowance every month. On the free plan the balance resets monthly with no carry-over. On a paid plan, unused credits carry into the next month once and expire if still unused at the end of it; credits bought as a top-up never expire, and carried credits are always spent first. Reviews of public arXiv papers do not consume credits.",
        links: [{ label: "See plans and prices", href: "/pricing" }],
      },
      {
        q: "Do writing and reviews share the same credits?",
        a: "Yes. Writing turns, compiles and AI reviews all draw on the same balance, each metered against what it actually costs to run. There is no separate writing subscription.",
      },
      {
        q: "What happens when I run out of credits?",
        a: "Metered actions pause until you top up or move to a larger plan. Nothing is charged automatically on top of your subscription, your documents stay exactly as they are, and reviews of public arXiv papers keep working.",
      },
    ],
  },
  {
    id: "privacy",
    category: "Privacy, data and account",
    items: [
      {
        q: "Is my unpublished draft safe on PaperVerse?",
        a: "Yes. Private drafts and writing projects are visible only to you and to anyone you explicitly invite. They are not listed, not published and not shared with other users. Pre-publication confidentiality is the whole point of uploading a draft, and we treat it that way.",
      },
      {
        q: "Is my work used to train AI models?",
        a: "No. Your manuscripts and drafts are not used to train our models, and we have contracted with our AI provider so that your content is not used to train theirs. Content is sent to the model only to carry out the action you asked for.",
        links: [{ label: "How we use AI", href: "/ai-transparency" }],
      },
      {
        q: "Where is my data processed?",
        a: "Your papers and AI-generated reviews are processed exclusively within the European Union. A few ancillary services (payment processing, search-engine submission, and frontend hosting) operate from the UK or USA but do not receive your confidential drafts. For the full provider list, see our Privacy Policy.",
        links: [{ label: "Privacy Policy", href: "/privacy-policy" }],
      },
      {
        q: "Can I control whether a paper is public or private?",
        a: "Yes. Anything you upload or write is private by default and stays private until you decide otherwise. Papers imported from arXiv are public, because they already are. You can publish a private paper later if you want to share its reviews with the community, and you can request export or deletion of your data from your account settings.",
      },
      {
        q: "How do I contact support?",
        a: "Email contact@paperverse.io. We usually reply within 24 hours — account questions, bug reports, feature requests and beta access requests all welcome.",
      },
    ],
  },
];

export const allFaqItems: FaqItem[] = faqData.flatMap((category) => category.items);
